import React, { useState } from 'react';
import { useMutation, useQuery, gql } from '@apollo/client';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import classNames from 'classnames';
import { Slideout } from '../slideout';
import { Radio } from '../checkbox-or-radio-field';
import { useTeamInfo } from '../../team-id-context';
import { ReactComponent as DocumentIcon } from '../../shared/icons/document-icon.svg';
import { ReactComponent as ExternalLinkIcon } from '../../shared/icons/external-open.svg';
import { GET_TEAM_CONTACT_FIELD_SCHEMAS } from '../../api/contacts/customFieldsApi';
import { useNotifications } from '../../notifications';
import styles from './styles.module.scss';

const CREATE_TEAM_CONTACT = gql`
  mutation CreateTeamContact(
    $teamId: ID!
    $name: String!
    $email: String
    $phone: String
    $company: String
    $title: String
    $address: String
    $contactType: String
    $customFields: JSON
  ) {
    createTeamContact(
      teamId: $teamId
      name: $name
      email: $email
      phone: $phone
      company: $company
      title: $title
      address: $address
      contactType: $contactType
      customFields: $customFields
    ) {
      id
      name
    }
  }
`;

const UPDATE_TEAM_CONTACT = gql`
  mutation UpdateTeamContact(
    $id: ID!
    $name: String
    $email: String
    $phone: String
    $company: String
    $title: String
    $address: String
    $contactType: String
    $customFields: JSON
  ) {
    updateTeamContact(
      id: $id
      name: $name
      email: $email
      phone: $phone
      company: $company
      title: $title
      address: $address
      contactType: $contactType
      customFields: $customFields
    ) {
      id
      name
    }
  }
`;

const GET_CONTACT_DOCUMENTS = gql`
  query GetContactBoundDocuments($teamId: ID!, $contactId: ID!) {
    teamContact(teamId: $teamId, id: $contactId) {
      id
      boundDocuments {
        id
        title
        status
        deal {
          id
          title
        }
      }
    }
  }
`;

interface Contact {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  title?: string;
  address?: string;
  contactType: 'person' | 'company';
  customFields?: Record<string, any>;
  teamCustomFields?: any[];
}

interface BoundDocument {
  id: string;
  title: string;
  status: string;
  deal: {
    id: string;
    title: string;
  };
}

interface ContactSlideoutProps {
  contact?: Contact;
  onClose: () => void;
  onSave: () => void;
}

type TabType = 'details' | 'documents';

const ContactSlideout: React.FC<ContactSlideoutProps> = ({ contact, onClose, onSave }) => {
  const isEditing = !!contact;
  const { teamId } = useTeamInfo();
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [, { success: notifySuccess }] = useNotifications();

  const [formData, setFormData] = useState({
    name: contact?.name || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    company: contact?.company || '',
    title: contact?.title || '',
    address: contact?.address || '',
    contactType: contact?.contactType || 'person',
    customFields: contact?.customFields || {},
  });

  const [errors, setErrors] = useState<string[]>([]);

  const [createContact, { loading: createLoading }] = useMutation(CREATE_TEAM_CONTACT);
  const [updateContact, { loading: updateLoading }] = useMutation(UPDATE_TEAM_CONTACT);

  // Only fetch documents when editing and documents tab is active
  const { data: documentsData, loading: documentsLoading } = useQuery(GET_CONTACT_DOCUMENTS, {
    variables: { teamId, contactId: contact?.id },
    skip: !isEditing || !contact?.id || activeTab !== 'documents',
    fetchPolicy: 'cache-and-network',
  });

  // Fetch custom field schemas for the team
  const { data: customFieldsData } = useQuery(GET_TEAM_CONTACT_FIELD_SCHEMAS, {
    variables: { teamId },
    skip: !teamId,
  });

  const customFieldSchemas = customFieldsData?.teamContactFieldSchemas || [];
  const boundDocuments: BoundDocument[] = documentsData?.teamContact?.boundDocuments || [];
  const isLoading = createLoading || updateLoading;

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors([]);
  };

  const handleCustomFieldChange = (fieldKey: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      customFields: { ...prev.customFields, [fieldKey]: value },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setErrors(['Name is required']);
      return;
    }

    try {
      if (isEditing) {
        await updateContact({
          variables: {
            id: contact.id,
            name: formData.name,
            email: formData.email || null,
            phone: formData.phone || null,
            company: formData.company || null,
            title: formData.title || null,
            address: formData.address || null,
            contactType: formData.contactType,
            customFields: formData.customFields,
          },
        });
        notifySuccess({ message: 'Contact updated successfully' });
      } else {
        await createContact({
          variables: {
            teamId,
            name: formData.name,
            email: formData.email || null,
            phone: formData.phone || null,
            company: formData.company || null,
            title: formData.title || null,
            address: formData.address || null,
            contactType: formData.contactType,
            customFields: formData.customFields,
          },
        });
        notifySuccess({ message: 'Contact created successfully' });
      }
      onSave();
    } catch (error: any) {
      console.error('Failed to save contact:', error);
      const message = error.graphQLErrors?.[0]?.message || error.message || 'Failed to save contact';
      setErrors([message]);
    }
  };

  // Deal routes are nested under the team; a bare /deals/... path matches no route.
  const getDocumentUrl = (doc: BoundDocument) => `/teams/${teamId}/deals/${doc.deal.id}/documents/${doc.id}`;

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').toLowerCase();
  };

  const slideoutTitle = (
    <div className={styles.headerWithPill}>
      <span>{isEditing ? 'Edit Contact' : 'New Contact'}</span>
      {isEditing && (
        <span className={classNames(styles.contactTypePill, {
          [styles.companyPill]: formData.contactType === 'company',
          [styles.personPill]: formData.contactType === 'person'
        })}>
          {formData.contactType === 'company' ? 'Company' : 'Person'}
        </span>
      )}
    </div>
  );

  return (
    <Slideout isOpen={true} onClose={onClose} title={slideoutTitle} width="medium">
      <div className={styles.slideoutContent}>
        {/* Tabs - only show when editing */}
        {isEditing && (
          <div className={styles.slideoutTabs}>
            <button
              type="button"
              className={classNames(styles.slideoutTab, { [styles.active]: activeTab === 'details' })}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button
              type="button"
              className={classNames(styles.slideoutTab, { [styles.active]: activeTab === 'documents' })}
              onClick={() => setActiveTab('documents')}
            >
              Associated Documents
            </button>
          </div>
        )}

        {/* Details Tab */}
        {activeTab === 'details' && (
          <Form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {/* Scrollable form content */}
            <div className={styles.slideoutFormContent}>
              {errors.length > 0 && (
                <div className={styles.errors}>
                  {errors.map((error, i) => (
                    <p key={i}>{error}</p>
                  ))}
                </div>
              )}

              {/* Contact Type - only show when creating */}
              {!isEditing && (
                <Form.Group className={styles.formGroup}>
                  <Form.Label>Contact Type</Form.Label>
                  <div className={styles.radioGroup}>
                    <Radio
                      id="contact-type-person"
                      name="contactType"
                      label="Person"
                      checked={formData.contactType === 'person'}
                      onChange={() => handleChange('contactType', 'person')}
                    />
                    <Radio
                      id="contact-type-company"
                      name="contactType"
                      label="Company"
                      checked={formData.contactType === 'company'}
                      onChange={() => handleChange('contactType', 'company')}
                    />
                  </div>
                </Form.Group>
              )}

              <Form.Group className={styles.formGroup}>
                <Form.Label>
                  {formData.contactType === 'company' ? 'Company Name' : 'Full Name'} <span className={styles.required}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder={formData.contactType === 'company' ? 'Acme Corp' : 'John Smith'}
                  required
                />
              </Form.Group>

              <Form.Group className={styles.formGroup}>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="email@example.com"
                />
              </Form.Group>

              <Form.Group className={styles.formGroup}>
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </Form.Group>

              {formData.contactType === 'person' && (
                <>
                  <Form.Group className={styles.formGroup}>
                    <Form.Label>Company</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.company}
                      onChange={(e) => handleChange('company', e.target.value)}
                      placeholder="Company name"
                    />
                  </Form.Group>

                  <Form.Group className={styles.formGroup}>
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      placeholder="Job title"
                    />
                  </Form.Group>
                </>
              )}

              <Form.Group className={styles.formGroup}>
                <Form.Label>Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Street address, city, state, ZIP"
                />
              </Form.Group>

              {/* Custom Fields Section */}
              {customFieldSchemas.length > 0 && (
                <>
                  <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
                  <h6 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#4a5568', marginBottom: '1rem' }}>
                    Custom Fields
                  </h6>

                  {customFieldSchemas.map((schema: any) => (
                    <Form.Group key={schema.id} className={styles.formGroup}>
                      <Form.Label>{schema.fieldLabel}</Form.Label>
                      <Form.Control
                        type={schema.fieldType === 'number' ? 'number' : schema.fieldType === 'date' ? 'date' : schema.fieldType === 'email' ? 'email' : schema.fieldType === 'phone' ? 'tel' : 'text'}
                        value={formData.customFields[schema.fieldKey] || ''}
                        onChange={(e) => handleCustomFieldChange(schema.fieldKey, e.target.value)}
                        placeholder={`Enter ${schema.fieldLabel.toLowerCase()}`}
                      />
                    </Form.Group>
                  ))}
                </>
              )}
            </div>

            {/* Sticky actions at bottom */}
            <div className={styles.slideoutActions}>
              <Button variant="link" className="text-secondary" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={isLoading}>
                {isLoading ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Contact'}
              </Button>
            </div>
          </Form>
        )}

        {/* Associated Documents Tab */}
        {activeTab === 'documents' && (
          <div className={styles.slideoutTabContent}>
            {documentsLoading ? (
              <div className={styles.loadingDocuments}>Loading documents...</div>
            ) : boundDocuments.length === 0 ? (
              <div className={styles.emptyDocuments}>
                <DocumentIcon width={40} height={40} />
                <p>No documents are associated with this contact yet.</p>
              </div>
            ) : (
              <div className={styles.documentsList}>
                {boundDocuments.map((doc) => (
                  <a
                    key={doc.id}
                    href={getDocumentUrl(doc)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.documentItem}
                  >
                    <DocumentIcon width={20} height={20} />
                    <div className={styles.documentItemInfo}>
                      <div className={styles.documentItemName}>{doc.title}</div>
                      <div className={styles.documentItemMeta}>
                        <span>{doc.deal.title}</span>
                        <span className={styles.documentItemStatus}>{formatStatus(doc.status)}</span>
                      </div>
                    </div>
                    <ExternalLinkIcon width={16} height={16} />
                  </a>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Slideout>
  );
};

export default ContactSlideout;
